import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductDetail, CoatingCompatibility, Comment } from '../../../../types/ProductTypes';
import { SectionCard, SectionTitle, FullWidthFields } from '../../Section.styles';
import queryParamsUtils from '../../../../utils/queryParamsUtils';
import useGetCoatingNotes, { CoatingNote } from '../../../../api/useGetCoatingNotes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import {
    CoatingTable,
    CoatingName,
    CompatibilityBadge,
    CoatingNotesList,
    CoatingNoteLink,
    ManufacturerCell,
    ManufacturerLink,
    ManufacturerName,
    TemperatureCell,
    NoCoatingsMessage,
    CoatingCommentsSection,
    CommentsLabel,
    CommentsText,
    CommentsTextarea,
    EditSelect,
    EditInput,
    RemoveButton,
    ActionCell,
    CategoryLink,
    NotesEditContainer,
    NoteItem,
    NoteNumber,
    NoteSelect,
    RemoveNoteButton,
} from './CoatingCompatibilitiesSection.styles';

interface RelatedProduct {
    id: number;
    categoryName: string;
    tradeName: string | null;
    chartererName: string | null;
    synonyms: string;
    isActive?: boolean;
}

interface CoatingCompatibilitiesSectionProps {
    product: ProductDetail;
    relatedProducts: RelatedProduct[];
    onCoatingCompatibilitiesChange?: (coatingCompatibilities: CoatingCompatibility[]) => void;
    onCommentsChange?: (comments: Comment[]) => void;
}

// Compatibility values matching the old implementation enum
enum CompatibilityLevel {
    NOT_ASSESSED = 0,
    YES = 1,
    RESTRICTIONS = 2,
    NO = 3,
}

const compatibilityTextMap: Record<CompatibilityLevel, string> = {
    [CompatibilityLevel.NOT_ASSESSED]: 'Not Assessed',
    [CompatibilityLevel.YES]: 'Yes',
    [CompatibilityLevel.RESTRICTIONS]: 'Restrictions',
    [CompatibilityLevel.NO]: 'No',
};

const compatibilityOptions = [
    { value: CompatibilityLevel.NOT_ASSESSED, text: compatibilityTextMap[CompatibilityLevel.NOT_ASSESSED] },
    { value: CompatibilityLevel.YES, text: compatibilityTextMap[CompatibilityLevel.YES] },
    { value: CompatibilityLevel.RESTRICTIONS, text: compatibilityTextMap[CompatibilityLevel.RESTRICTIONS] },
    { value: CompatibilityLevel.NO, text: compatibilityTextMap[CompatibilityLevel.NO] },
];

// Manufacturer logo/link configuration (matching old implementation)
const manufacturerConfig: Record<
    number,
    {
        name: string;
        logo: string;
        url: string;
        resistanceList?: string;
        generalNote?: string;
    }
> = {
    1: {
        name: 'Sigma',
        logo: 'Sigma_logo.png',
        url: 'http://tris.sigmacoatings.com/',
        resistanceList: 'Resistance list Sigma.pdf',
    },
    2: {
        name: 'Carboline',
        logo: 'Carboline_logo.png',
        url: 'http://www.carboline.com/',
        resistanceList: 'Resistance list Carboline.pdf',
    },
    3: {
        name: 'International',
        logo: 'International_logo.png',
        url: 'http://www.international-marine.com/supportadvice/cargo-resistance-guide.aspx',
        resistanceList: 'Resistance list International.pdf',
    },
    4: {
        name: 'Amercoat',
        logo: 'PPG_Amercoat_logo.gif',
        url: 'http://ppgamercoatus.ppgpmc.com/',
        resistanceList: 'Resistance list Amercoat.pdf',
    },
    5: {
        name: 'Jotun',
        logo: 'Jotun_logo.png',
        url: 'http://jotunprl.com/Search.aspx?c=1',
        resistanceList: 'Resistance list Jotun.pdf',
    },
    7: {
        name: 'Advanced Polymer Coatings',
        logo: 'APC_logo.gif',
        url: 'http://www.adv-polymer.com/Marine_Transportation_Protective_Coatings/MarineLine784.asp',
        resistanceList: 'Resistance list Advanced Polymer Coatings.pdf',
    },
    10: {
        name: 'Jotun Tankguard Flexline',
        logo: 'Jotun_logo.png',
        url: 'http://jotunprl.com/Search.aspx?c=1',
        resistanceList: 'Resistance list Jotun Tankguard Flexline.pdf',
    },
    11: {
        name: 'Chugoku',
        logo: 'cmp_logo.png',
        url: 'https://www.cmp-chugoku.com/global.html',
        resistanceList: 'Resistance list Chugoku.pdf',
    },
};

const CoatingCompatibilitiesSection: React.FC<CoatingCompatibilitiesSectionProps> = ({
    product,
    relatedProducts,
    onCoatingCompatibilitiesChange,
    onCommentsChange,
}) => {
    const navigate = useNavigate();
    const { setQueryParamProductId } = queryParamsUtils();
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();

    // ProductEditContext may not be available if provider is not in the tree
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;

    // Fetch all coating notes for the dropdown
    const { coatingNotes: allCoatingNotes, getCoatingNotes } = useGetCoatingNotes();

    // Local state for editing
    const [localCoatings, setLocalCoatings] = useState<CoatingCompatibility[]>(product.coatingCompatibilities || []);
    const [localComments, setLocalComments] = useState<Comment[]>(product.comments || []);

    // Fetch coating notes when entering edit mode
    useEffect(() => {
        if (isInEditMode && canEdit) {
            getCoatingNotes();
        }
    }, [isInEditMode, canEdit, getCoatingNotes]);

    // Update local state when product changes
    React.useEffect(() => {
        setLocalCoatings(product.coatingCompatibilities || []);
    }, [product.coatingCompatibilities]);

    React.useEffect(() => {
        setLocalComments(product.comments || []);
    }, [product.comments]);

    const activeCoatings = localCoatings.filter((c) => c.coating.isActive);

    // Get the category product ID (product with no tradeName and no chartererName)
    // This matches the old implementation's getCategoryProductId() function
    const categoryProductId = useMemo(() => {
        if (relatedProducts && relatedProducts.length > 0) {
            const categoryProduct = relatedProducts.find((p) => (!p.tradeName || p.tradeName === '') && (!p.chartererName || p.chartererName === ''));
            return categoryProduct?.id ?? null;
        }
        return null;
    }, [relatedProducts]);

    // Get comments - Category 2 (Coating Comments) and Category 14 (Coating Comments MSG)
    const coatingComments = localComments.find((c) => c.category === 2);
    const coatingCommentsMSG = localComments.find((c) => c.category === 14);

    // Notify parent of coating changes
    const notifyCoatingChange = useCallback(
        (updatedCoatings: CoatingCompatibility[]) => {
            setLocalCoatings(updatedCoatings);
            if (mergePendingChanges) {
                mergePendingChanges({ coatingCompatibilities: updatedCoatings });
            }
            if (onCoatingCompatibilitiesChange) {
                onCoatingCompatibilitiesChange(updatedCoatings);
            }
        },
        [onCoatingCompatibilitiesChange, mergePendingChanges]
    );

    // Notify parent of comment changes
    const notifyCommentChange = useCallback(
        (updatedComments: Comment[]) => {
            setLocalComments(updatedComments);
            if (mergePendingChanges) {
                // Get current pending changes to properly merge comments from other sections
                const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
                const baseComments = currentPendingChanges.comments || product.comments || [];

                // Find what categories we're updating (2 and 14 are coating comment categories)
                const updatingCategories = new Set(updatedComments.map((c) => c.category));

                // Keep comments from other categories that aren't being updated
                const otherComments = baseComments.filter((c: Comment) => !updatingCategories.has(c.category));

                // Merge: keep other section's comments + our updated comments
                const mergedComments = [...otherComments, ...updatedComments];

                mergePendingChanges({ comments: mergedComments });
            } else {
                console.warn('[CoatingCompatibilitiesSection.notifyCommentChange] mergePendingChanges is not available!');
            }
            if (onCommentsChange) {
                onCommentsChange(updatedComments);
            }
        },
        [onCommentsChange, mergePendingChanges, getPendingChanges, product.comments]
    );

    // Handle compatibility change
    const handleCompatibilityChange = useCallback(
        (coatingId: number, newValue: number) => {
            const updatedCoatings = localCoatings.map((c) => (c.id === coatingId ? { ...c, compatibility: newValue } : c));
            notifyCoatingChange(updatedCoatings);
        },
        [localCoatings, notifyCoatingChange]
    );

    // Handle max temperature change
    const handleMaxTemperatureChange = useCallback(
        (coatingId: number, newValue: string) => {
            const numValue = newValue === '' ? null : parseFloat(newValue);
            const updatedCoatings = localCoatings.map((c) => (c.id === coatingId ? { ...c, maxTemperature: numValue } : c));
            notifyCoatingChange(updatedCoatings);
        },
        [localCoatings, notifyCoatingChange]
    );

    // Handle remove coating compatibility
    const handleRemoveCoating = useCallback(
        (coatingId: number) => {
            const updatedCoatings = localCoatings.filter((c) => c.id !== coatingId);
            notifyCoatingChange(updatedCoatings);
        },
        [localCoatings, notifyCoatingChange]
    );

    // Handle add coating note
    const handleAddCoatingNote = useCallback(
        (coatingId: number, noteId: number) => {
            const noteToAdd = allCoatingNotes.find((n) => n.id === noteId);
            if (!noteToAdd) return;

            const updatedCoatings = localCoatings.map((c) => {
                if (c.id === coatingId) {
                    const existingNotes = c.coatingNotes || [];
                    // Don't add if already exists
                    if (existingNotes.some((n: any) => n.id === noteId)) {
                        return c;
                    }
                    return {
                        ...c,
                        coatingNotes: [...existingNotes, noteToAdd],
                    };
                }
                return c;
            });
            notifyCoatingChange(updatedCoatings);
        },
        [localCoatings, allCoatingNotes, notifyCoatingChange]
    );

    // Handle remove coating note
    const handleRemoveCoatingNote = useCallback(
        (coatingId: number, noteId: number) => {
            const updatedCoatings = localCoatings.map((c) => {
                if (c.id === coatingId) {
                    return {
                        ...c,
                        coatingNotes: (c.coatingNotes || []).filter((n: any) => n.id !== noteId),
                    };
                }
                return c;
            });
            notifyCoatingChange(updatedCoatings);
        },
        [localCoatings, notifyCoatingChange]
    );

    // Get selectable notes for a coating (filtered by manufacturer)
    const getSelectableNotes = useCallback(
        (compatibility: CoatingCompatibility): CoatingNote[] => {
            const manufacturerId = compatibility.coating.manufacturer.id;
            const existingNoteIds = (compatibility.coatingNotes || []).map((n: any) => n.id);

            return allCoatingNotes.filter((note) => note.manufacturerId === manufacturerId && !existingNoteIds.includes(note.id));
        },
        [allCoatingNotes]
    );

    // Handle comment change
    const handleCommentChange = useCallback(
        (category: number, newText: string) => {
            const existingComment = localComments.find((c) => c.category === category);
            let updatedComments: Comment[];

            if (existingComment) {
                updatedComments = localComments.map((c) => (c.category === category ? { ...c, text: newText } : c));
            } else {
                // Create new comment if it doesn't exist
                const newComment: Comment = {
                    id: 0, // New comment, will be assigned by server
                    text: newText,
                    category: category,
                    visibility: 1,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                updatedComments = [...localComments, newComment];
            }
            notifyCommentChange(updatedComments);
        },
        [localComments, notifyCommentChange]
    );

    const getCompatibilityText = (level: number): string => {
        return compatibilityTextMap[level as CompatibilityLevel] ?? compatibilityTextMap[CompatibilityLevel.NOT_ASSESSED];
    };

    const hasManufacturerInfo = (manufacturerId: number): boolean => {
        return manufacturerId in manufacturerConfig;
    };

    const renderManufacturer = (manufacturerId: number, manufacturerName: string) => {
        if (hasManufacturerInfo(manufacturerId)) {
            return (
                <ManufacturerCell>
                    <ManufacturerLink
                        href={manufacturerConfig[manufacturerId].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Visit ${manufacturerConfig[manufacturerId].name} website`}
                    >
                        {manufacturerName}
                    </ManufacturerLink>
                </ManufacturerCell>
            );
        }
        return <ManufacturerName>{manufacturerName}</ManufacturerName>;
    };

    // For Annex II - Chapter 17 products, show link to category level
    const isAnnexIIChapter17 = product.category?.regulations?.marpolRegulation === 2;
    const isProductCategory = !product.tradeName; // Product categories don't have trade names

    const handleCategoryClick = (productId: number) => {
        navigate('/product');
        setQueryParamProductId(productId.toString());
    };

    if (isAnnexIIChapter17 && !isProductCategory) {
        return (
            <SectionCard>
                <SectionTitle>Coating Resistance List</SectionTitle>
                <p>Coating resistance information is maintained at the product category level.</p>
                {product.category && categoryProductId && (
                    <>
                        <p>
                            <strong>View coating resistance for this product category:</strong>
                        </p>
                        <p>
                            <CategoryLink
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCategoryClick(categoryProductId);
                                }}
                            >
                                {product.category.name}
                            </CategoryLink>
                        </p>
                    </>
                )}
            </SectionCard>
        );
    }

    // Check if we should show edit controls
    const showEditControls = isInEditMode && canEdit;

    return (
        <SectionCard>
            <SectionTitle>Coating Resistance List</SectionTitle>

            {activeCoatings.length === 0 && !showEditControls ? (
                <NoCoatingsMessage>No coating resistance information.</NoCoatingsMessage>
            ) : (
                <CoatingTable>
                    <thead>
                        <tr>
                            {showEditControls && <th className="action-col"></th>}
                            <th>Name</th>
                            <th>Resistance</th>
                            <th>Notes</th>
                            <th>Manufacturer</th>
                            <th className="max-temp-col">Max Temperature</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeCoatings
                            .sort((a, b) => a.coating.name.localeCompare(b.coating.name))
                            .map((compatibility) => (
                                <tr key={compatibility.id}>
                                    {showEditControls && (
                                        <td>
                                            <ActionCell>
                                                <RemoveButton
                                                    type="button"
                                                    onClick={() => handleRemoveCoating(compatibility.id)}
                                                    title="Remove Coating Compatibility"
                                                >
                                                    🗑️
                                                </RemoveButton>
                                            </ActionCell>
                                        </td>
                                    )}
                                    <td>
                                        <CoatingName>{compatibility.coating.name}</CoatingName>
                                    </td>
                                    <td>
                                        {showEditControls ? (
                                            <EditSelect
                                                value={compatibility.compatibility}
                                                onChange={(e) => handleCompatibilityChange(compatibility.id, parseInt(e.target.value, 10))}
                                            >
                                                {compatibilityOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.text}
                                                    </option>
                                                ))}
                                            </EditSelect>
                                        ) : (
                                            <CompatibilityBadge level={compatibility.compatibility}>
                                                {getCompatibilityText(compatibility.compatibility)}
                                            </CompatibilityBadge>
                                        )}
                                    </td>
                                    <td>
                                        {showEditControls ? (
                                            <NotesEditContainer>
                                                {/* Show existing notes with remove buttons */}
                                                {compatibility.coatingNotes &&
                                                    compatibility.coatingNotes.map((note: any, index: number) => (
                                                        <NoteItem key={note.id || index}>
                                                            <RemoveNoteButton
                                                                type="button"
                                                                onClick={() => handleRemoveCoatingNote(compatibility.id, note.id)}
                                                                title="Remove Coating Note"
                                                            >
                                                                🗑️
                                                            </RemoveNoteButton>
                                                            <NoteNumber>{note.number}</NoteNumber>
                                                        </NoteItem>
                                                    ))}
                                                {/* Dropdown to add new notes (filtered by manufacturer) */}
                                                {getSelectableNotes(compatibility).length > 0 && (
                                                    <NoteSelect
                                                        value=""
                                                        onChange={(e) => {
                                                            if (e.target.value) {
                                                                handleAddCoatingNote(compatibility.id, parseInt(e.target.value, 10));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Add note...</option>
                                                        {getSelectableNotes(compatibility).map((note) => (
                                                            <option key={note.id} value={note.id}>
                                                                {note.number}
                                                            </option>
                                                        ))}
                                                    </NoteSelect>
                                                )}
                                            </NotesEditContainer>
                                        ) : compatibility.coatingNotes && compatibility.coatingNotes.length > 0 ? (
                                            <CoatingNotesList>
                                                {compatibility.coatingNotes.map((note: any, index: number) => (
                                                    <CoatingNoteLink
                                                        key={note.id || index}
                                                        title={`${note.manufacturer?.name || ''} - ${note.number}\n${note.text || ''}`}
                                                    >
                                                        {note.number}
                                                    </CoatingNoteLink>
                                                ))}
                                            </CoatingNotesList>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </td>
                                    <td>{renderManufacturer(compatibility.coating.manufacturer.id, compatibility.coating.manufacturer.name)}</td>
                                    <td>
                                        {showEditControls ? (
                                            <EditInput
                                                type="number"
                                                value={compatibility.maxTemperature ?? ''}
                                                onChange={(e) => handleMaxTemperatureChange(compatibility.id, e.target.value)}
                                                placeholder=""
                                                min="-273"
                                                max="999"
                                                step="0.01"
                                            />
                                        ) : compatibility.maxTemperature !== null && compatibility.maxTemperature !== undefined ? (
                                            <TemperatureCell>{compatibility.maxTemperature}°C</TemperatureCell>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </CoatingTable>
            )}

            {/* Coating Comments (Category 2) - visible in edit mode OR when text exists */}
            {(isInEditMode || (coatingComments && coatingComments.text)) && (
                <CoatingCommentsSection>
                    <CommentsLabel>Comments:</CommentsLabel>
                    <FullWidthFields>
                        {showEditControls ? (
                            <CommentsTextarea
                                rows={3}
                                value={coatingComments?.text || ''}
                                onChange={(e) => handleCommentChange(2, e.target.value)}
                                placeholder="Enter coating comments..."
                            />
                        ) : (
                            <CommentsText>{coatingComments?.text}</CommentsText>
                        )}
                    </FullWidthFields>
                </CoatingCommentsSection>
            )}

            {/* Coating Comments MSG (Category 14) - only visible to editors, in edit mode OR when text exists */}
            {canEdit && (isInEditMode || (coatingCommentsMSG && coatingCommentsMSG.text)) && (
                <CoatingCommentsSection>
                    <CommentsLabel>Comments (MSG):</CommentsLabel>
                    <FullWidthFields>
                        {showEditControls ? (
                            <CommentsTextarea
                                rows={3}
                                value={coatingCommentsMSG?.text || ''}
                                onChange={(e) => handleCommentChange(14, e.target.value)}
                                placeholder="Enter MSG coating comments..."
                            />
                        ) : (
                            <CommentsText>{coatingCommentsMSG?.text}</CommentsText>
                        )}
                    </FullWidthFields>
                </CoatingCommentsSection>
            )}
        </SectionCard>
    );
};

export default CoatingCompatibilitiesSection;
